import { logger } from '../utils/logger';

// --- BESS Revenue Models ---
export const calculateBessRevenueStreams = (inputs, solarGeneration) => {
    if (!inputs.bessEnabled) return { arbitrageRevenue: 0, ancillaryRevenue: 0, capacityRevenue: 0, demandChargeReduction: 0, totalBessRevenue: 0 };
    
    // Energy Arbitrage: Buy low, sell high
    const arbitrageCycles = inputs.bessArbitrageCycles; // cycles per day
    const arbitrageSpread = inputs.onGridTariff * inputs.bessArbitrageSpread; // price differential
    const arbitrageEfficiency = inputs.bessRoundTripEfficiency;
    const arbitrageRevenue = inputs.bessEnergyCapacity * arbitrageCycles * 365 * arbitrageSpread * arbitrageEfficiency;
    
    // Ancillary Services (frequency regulation, etc.)
    const ancillaryRevenue = inputs.bessPowerCapacity * inputs.bessAncillaryRate; // $/MW/year
    
    // Capacity/Demand Charge Reduction
    const capacityRevenue = inputs.bessPowerCapacity * inputs.bessCapacityRate * 12; // $/MW/month
    
    // Solar + Storage Value Stacking
    const demandChargeReduction = inputs.bessPowerCapacity * inputs.bessDemandChargeReduction * 12;
    
    const totalBessRevenue = arbitrageRevenue + ancillaryRevenue + capacityRevenue + demandChargeReduction;
    
    logger.debug('BESS revenue calculated', {
        arbitrageRevenue,
        ancillaryRevenue,
        capacityRevenue,
        demandChargeReduction,
        totalBessRevenue
    });
    
    return {
        arbitrageRevenue,
        ancillaryRevenue,
        capacityRevenue,
        demandChargeReduction,
        totalBessRevenue
    };
};

// --- BESS Investment Calculations ---
export const calculateBessInvestment = (inputs) => {
    if (!inputs.bessEnabled) {
        return {
            bessInvestment: 0,
            energyCost: 0,
            powerCost: 0
        };
    }
    
    const energyCost = inputs.bessEnergyCapacity * inputs.bessCostPerMWh;
    const powerCost = inputs.bessPowerCapacity * inputs.bessPowerCostPerMW;
    const bessInvestment = energyCost + powerCost;
    
    logger.debug('BESS investment calculated', {
        energyCost,
        powerCost,
        bessInvestment
    });
    
    return {
        bessInvestment,
        energyCost,
        powerCost
    };
};

// --- BESS Degradation Calculations ---
export const calculateBessDegradation = (inputs, yearIndex) => {
    if (!inputs.bessEnabled) {
        return {
            degradationFactor: 1,
            remainingCapacity: 0
        };
    }
    
    const degradationFactor = Math.pow((1 - inputs.bessDegradationRate), yearIndex);
    const remainingCapacity = inputs.bessEnergyCapacity * degradationFactor;
    
    logger.debug('BESS degradation calculated', {
        yearIndex,
        degradationRate: inputs.bessDegradationRate,
        degradationFactor,
        remainingCapacity
    });
    
    return {
        degradationFactor,
        remainingCapacity
    };
};

// --- BESS Replacement Cost Calculations ---
export const calculateBessReplacementCosts = (inputs, year) => {
    if (!inputs.bessEnabled) {
        return {
            isReplacementYear: false,
            replacementCost: 0
        };
    }
    
    const isReplacementYear = year > 0 && year % inputs.bessReplacementPeriod === 0 && year < inputs.operationPeriod;
    const replacementCost = isReplacementYear ? 
        (inputs.bessEnergyCapacity * inputs.bessCostPerMWh + inputs.bessPowerCapacity * inputs.bessPowerCostPerMW) * inputs.bessReplacementCostFactor :
        0;
    
    if (isReplacementYear) {
        logger.info('BESS replacement scheduled', {
            year,
            replacementCost,
            replacementFactor: inputs.bessReplacementCostFactor
        });
    }
    
    return {
        isReplacementYear,
        replacementCost
    };
};